import React from 'react';
import { useSelector } from 'react-redux';

import { RouteComponentProps } from 'react-router';
import { FormattedMessageV2 } from '@/views/components/i18n';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import ImplicationsSection from './TaxAnalysisImplicationsSections';

import { getShortTermRows, getLongTermRows, getTotalEstimatedTaxesRow, getCarryForwardRows } from './utils/getImplicationsRows';

import ComparisonItem from '@/types/comparison-item';
import Comparison from '@/types/comparison';
import { ReduxState } from '@/redux/modules/types';

type TaxAnalysisTableProps = RouteComponentProps & {
  items: ComparisonItem[];
  comparison: Comparison;
  isEditPinned: boolean;
  popoverContainerRef?: HTMLElement | null;
};

const TaxAnalysisTable: React.FC<TaxAnalysisTableProps> = () => {
  const activeComparison = useSelector((state: ReduxState) => state.comparison.activeComparison);
  const originalComparisonItemId = activeComparison?.taxAnalysis?.originalComparisonItemId;
  const taxRegion = activeComparison?.taxAnalysis?.taxSettings?.region;

  if (!taxRegion) {
    return null;
  }

  const comparisonItem = activeComparison?.items.find(item => item._id === originalComparisonItemId);
  const taxImplicationsData = comparisonItem?.taxAnalysis?.taxMetrics?.taxImplications;

  // Extract region-specific tax implications (calculator returns { US: {...} } or { CAN: {...} })
  const taxImplications = taxImplicationsData?.[taxRegion as keyof typeof taxImplicationsData];

  if (!taxImplications) {
    return null;
  }

  const sections = [
    {
      captionKey: 'Comparison.TaxAnalysis.ImplicationsTable.ShortTerm.Header',
      rows: getShortTermRows(),
      key: 'shortTerm',
    },
    {
      captionKey: 'Comparison.TaxAnalysis.ImplicationsTable.LongTerm.Header',
      rows: getLongTermRows(),
      key: 'longTerm',
    },
    {
      rows: getTotalEstimatedTaxesRow(),
      key: 'totalEstimatedTaxes',
    },
    {
      captionKey: 'Comparison.TaxAnalysis.ImplicationsTable.CarryForward.Header',
      rows: getCarryForwardRows(),
      key: 'carryForward',
    },
  ];

  return (
    <div className="tax-transition-implications-table-container">
      <div className="transition-implications-table-card">
        <div className="tax-analysis-table-card-header">
          <FormattedMessageV2 tagName={ALLOWED_HTML_TAGS.H3} toLocalize="Comparison.TaxAnalysis.ImplicationsTable.Title" />
        </div>

        <div className="tax-analysis-table-scroll-wrapper">
          {sections.map(section => (
            <ImplicationsSection
              key={section.key}
              captionKey={section.captionKey}
              sectionKey={section.key}
              rows={section.rows}
              taxAnalysisData={taxImplications as any} // TODO: Clean when we add support for CA tax implications and can type this properly instead of using 'any'
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TaxAnalysisTable;
